/**
 * Commerce · Overview.
 */
import { TrendingUp, Sparkles } from "lucide-react";
import { Card, SectionHeader, DeltaChip } from "../../components/lib";
import { CanonicalKpiCell } from "../../components/CanonicalKpiCell";
import { useCanonicalKpi } from "../../hooks/useCanonicalKpi";
import { COMMERCE_OVERVIEW, SIGNALS } from "../../mocks/commerceData";

export default function Overview() {
  const { data: kpi, loading, error } = useCanonicalKpi("orvan");
  const days = COMMERCE_OVERVIEW.revenueByDay;
  const max = Math.max(...days.map((d) => d.v));

  const priority = SIGNALS.filter((s) => s.aiConfidence >= 0.85).slice(0, 4);
  const legacyKpis = (COMMERCE_OVERVIEW.kpis || []).filter((k) => k.key !== "revenue" && k.key !== "orders" && k.key !== "orders30d");

  return (
    <div className="space-y-6" data-testid="cm-overview">
      <Card testId="cm-kpis">
        <SectionHeader eyebrow="30-day" title="Commerce Performance"
          action={<span className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-ink-muted)" }} data-testid="cm-kpi-source-tag">Source: /api/pureh/kpi/summary</span>} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <CanonicalKpiCell
            testId="cm-kpi-revenue"
            label="Revenue (MTD)"
            value={loading ? null : (kpi?.revenue.monthlyEGP.value ?? 0)}
            unit="EGP"
            source={kpi?.revenue.sourceCollection || "pureh_customer_payments"}
            footnote={error ? "endpoint unavailable" : (kpi?.dataMode === "demo" ? "demo mode" : undefined)}
            empty={loading}
          />
          <CanonicalKpiCell
            testId="cm-kpi-orders-30d"
            label="Orders · 30d"
            value={loading ? null : (kpi?.orders.last30d.value ?? 0)}
            unit={null}
            source={kpi?.orders.sourceCollection || "pureh_orders"}
          />
          <CanonicalKpiCell
            testId="cm-kpi-live-skus"
            label="Live SKUs"
            value={loading ? null : (kpi?.products.liveSkus.value ?? 0)}
            unit={null}
            source={kpi?.products.sourceCollection || "pureh_product_master"}
          />
          {legacyKpis.slice(0, 3).map((k) => (
            <div key={k.key} data-testid={`cm-kpi-${k.key}`}>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-ink-muted)" }}>{k.label}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)", letterSpacing: "-0.01em" }}>{k.value}</div>
              <div className="mt-1 flex items-center gap-2">
                <DeltaChip delta={k.delta} trend={k.trend as any} />
                {k.hint && <span className="text-[10px]" style={{ color: "var(--ph-ink-soft)" }}>{k.hint}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card testId="cm-revenue-week" className="lg:col-span-2">
          <SectionHeader eyebrow="This week" title="Revenue by day" />
          <div className="flex items-end gap-3 h-40 pt-2" data-testid="cm-revenue-bars">
            {days.map((d) => (
              <div key={d.d} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end" style={{ height: 130 }}>
                  <div
                    style={{
                      width: "100%",
                      height: `${(d.v / max) * 100}%`,
                      background: "var(--ph-brand-primary)",
                      borderRadius: "var(--ph-radius-sm) var(--ph-radius-sm) 0 0",
                    }}
                  />
                </div>
                <div className="text-[10px] font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{d.v}K</div>
                <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>{d.d}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card testId="cm-top-products">
          <SectionHeader eyebrow="30-day" title="Top products" />
          <div className="space-y-3">
            {COMMERCE_OVERVIEW.topProducts.map((p, i) => (
              <div key={p.sku} data-testid={`cm-top-${p.sku}`} className="flex items-center gap-3">
                <div
                  className="w-6 h-6 flex items-center justify-center text-[10px] font-semibold tabular-nums flex-shrink-0"
                  style={{ background: "var(--ph-brand-tint)", color: "var(--ph-brand-primary)", borderRadius: "999px" }}
                >
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: "var(--ph-ink-strong)" }}>{p.name}</div>
                  <div className="text-[11px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>{p.unitsSold} units</div>
                </div>
                <div className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-ink-strong)" }}>
                  EGP {(p.revenueEGP / 1000).toFixed(0)}K
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card testId="cm-ai-priorities">
        <SectionHeader eyebrow="AI" title="Priority signals" action={<Sparkles size={14} style={{ color: "var(--ph-ai)" }} />} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {priority.map((s) => (
            <div key={s.id} data-testid={`cm-priority-${s.id}`} className="p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", border: "1px solid var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ai)" }}>{s.kind.replace("-", " ")}</div>
              <div className="text-sm font-semibold mt-1" style={{ color: "var(--ph-ink-strong)" }}>{s.customer} · {s.city}</div>
              <div className="text-xs mt-1" style={{ color: "var(--ph-ink-muted)" }}>{s.detail}</div>
              <div className="text-[11px] mt-2" style={{ color: "var(--ph-ink-default)" }}><b>Next:</b> {s.suggestedAction}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
